import { useState } from "react";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { preselectedPlan, recommendPlan } from "@/lib/onboarding/recommendPlan";
import type { GiftsAnswer, GuestsAnswer, OnboardingAnswers, PlanChoice } from "@/lib/onboarding/types";

type PackageAnswers = NonNullable<OnboardingAnswers["packageAnswers"]>;

interface PlanRecommendationCardProps {
  answers: PackageAnswers | null;
  /** Paid plan picked at registration (?buyPlan= / planSelectionStore). */
  pendingPlan: PlanChoice | null;
  busy: boolean;
  onAnswers: (answers: PackageAnswers) => void;
  onCheckout: (plan: PlanChoice) => void;
}

function Segmented<T extends string>({
  label,
  name,
  options,
  value,
  onChange,
  labelOf,
}: {
  label: string;
  name: string;
  options: T[];
  value: T | null;
  onChange: (value: T) => void;
  labelOf: (value: T) => string;
}) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">{label}</legend>
      <div className="flex flex-wrap gap-2" role="radiogroup">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={value === option}
            onClick={() => onChange(option)}
            data-testid={`onboarding-plan-${name}-${option}`}
            className={cn(
              "rounded-full border px-3 py-1.5 text-sm transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500",
              value === option ? "border-pink-500 bg-pink-500 text-white" : "border-input bg-background hover:border-pink-300",
            )}
          >
            {labelOf(option)}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * JUB-160: non-blocking "Alege pachetul potrivit" card on the preview.
 * Two quick questions recommend a plan from the PlanService limits; the
 * customer may always stay on Free (§17: payment is never a wizard step).
 * Choosing a paid plan saves, completes the wizard and opens the
 * event-bound checkout for this event.
 */
export function PlanRecommendationCard({ answers, pendingPlan, busy, onAnswers, onCheckout }: PlanRecommendationCardProps) {
  const { t } = useTranslation();
  const [guests, setGuests] = useState<GuestsAnswer | null>(answers?.guests ?? null);
  const [gifts, setGifts] = useState<GiftsAnswer | null>(answers?.gifts ?? null);
  const [stayedFree, setStayedFree] = useState(answers?.choice === "free");

  const recommended = recommendPlan({ guests, gifts });
  const plan = preselectedPlan(recommended, pendingPlan);
  const planName = (p: PlanChoice) => t(`onboarding.package.plans.${p}`);

  const update = (next: { guests?: GuestsAnswer; gifts?: GiftsAnswer }) => {
    const g = next.guests ?? guests;
    const f = next.gifts ?? gifts;
    if (next.guests) setGuests(next.guests);
    if (next.gifts) setGifts(next.gifts);
    setStayedFree(false);
    onAnswers({ guests: g, gifts: f, choice: null });
  };

  const stayFree = () => {
    setStayedFree(true);
    onAnswers({ guests, gifts, choice: "free" });
  };

  return (
    <section
      className="space-y-4 rounded-2xl border-2 border-pink-100 bg-white p-5 shadow-sm dark:border-pink-900/40 dark:bg-gray-900"
      aria-labelledby="onboarding-plan-title"
      data-testid="onboarding-plan-card"
    >
      <div className="flex items-start gap-3">
        <Sparkles className="mt-0.5 h-5 w-5 text-pink-500" aria-hidden="true" />
        <div>
          <h2 id="onboarding-plan-title" className="text-lg font-semibold">
            {t("onboarding.package.title")}
          </h2>
          <p className="text-sm text-muted-foreground">{t("onboarding.package.subtitle")}</p>
        </div>
      </div>

      <Segmented<GuestsAnswer>
        label={t("onboarding.package.guestsLabel")}
        name="guests"
        options={["up_to_50", "up_to_200", "over_200"]}
        value={guests}
        onChange={(value) => update({ guests: value })}
        labelOf={(value) => t(`onboarding.package.guests.${value}`)}
      />
      <Segmented<GiftsAnswer>
        label={t("onboarding.package.giftsLabel")}
        name="gifts"
        options={["none", "envelope", "list"]}
        value={gifts}
        onChange={(value) => update({ gifts: value })}
        labelOf={(value) => t(`onboarding.package.gifts.${value}`)}
      />

      {pendingPlan && pendingPlan !== "free" && (
        <p className="text-sm text-muted-foreground">{t("onboarding.package.pendingNote", { plan: planName(pendingPlan) })}</p>
      )}

      {plan && (
        <div className="rounded-xl bg-pink-50 p-3 dark:bg-pink-950/30" aria-live="polite" data-testid="onboarding-plan-recommendation" data-plan={plan}>
          <p className="font-semibold">{t("onboarding.package.recommended", { plan: planName(plan) })}</p>
          <p className="text-sm text-muted-foreground">{t(`onboarding.package.reason.${plan}`)}</p>
        </div>
      )}

      {stayedFree ? (
        <p className="text-sm text-emerald-700 dark:text-emerald-400" role="status" data-testid="onboarding-plan-stayed-free">
          {t("onboarding.package.stayedFree")}
        </p>
      ) : (
        <div className="flex flex-col gap-2 sm:flex-row">
          {plan && plan !== "free" && (
            <Button
              type="button"
              className="bg-gradient-to-r from-pink-500 to-rose-500 text-white hover:from-pink-600 hover:to-rose-600"
              onClick={() => onCheckout(plan)}
              disabled={busy}
              data-testid="onboarding-plan-choose"
            >
              {t("onboarding.package.choose", { plan: planName(plan) })}
            </Button>
          )}
          {/* Always visible: the wizard never forces a purchase. */}
          <Button type="button" variant="outline" onClick={stayFree} disabled={busy} data-testid="onboarding-plan-stay-free">
            {t("onboarding.package.stayFree")}
          </Button>
        </div>
      )}
    </section>
  );
}
